import { Icon } from "../../../components/ui/Icon";

interface Props {
  /** What this surface would show, phrased as the thing, not the screen. */
  what: string;
  /** Why it cannot be shown, in one sentence. */
  detail: string;
  /** The endpoints this deployment would have to serve. `METHOD /path`. */
  endpoints: string[];
  testId?: string;
}

/**
 * Says, in place, that a designed surface has no data behind it here.
 *
 * Three of the canvas's administration surfaces — users and roles, the audit
 * log, and persisting the deployment identity — are drawn against fixture
 * content, and this application's API serves none of them. The screens are
 * still built: the layout, the controls, the states and the permission checks
 * are real, and the only thing missing is the data.
 *
 * So rather than inventing an endpoint, or shipping a screen that looks
 * finished and silently does nothing, the surface says which endpoints would
 * fill it. That is the honest form of "not yet": a reader can tell at a glance
 * that the screen is complete and the backend is not, and an implementer is
 * told exactly what to build.
 */
export function BackendUnavailable({
  what,
  detail,
  endpoints,
  testId,
}: Props): JSX.Element {
  return (
    <div className="set-unavailable" role="note" data-testid={testId ?? "backend-unavailable"}>
      <span className="set-unavailable-mark" aria-hidden>
        <Icon name="info" size={16} />
      </span>
      <div className="set-unavailable-copy">
        <strong>{what} is not available in this deployment</strong>
        <p>{detail}</p>
        {endpoints.length > 0 && (
          <ul className="set-unavailable-endpoints">
            {endpoints.map((endpoint) => (
              <li key={endpoint}>{endpoint}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
